import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import styled from 'styled-components';
// ui-kit
import { ProviderValidate, Inputs } from '@bagonboard/ui-kit';

// @material-ui/core components
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import InputAdornment from '@material-ui/core/InputAdornment';

// @material-ui/icons
import AccountCircle from '@material-ui/icons/AccountCircle';
import Email from '@material-ui/icons/Email';
import Lock from '@material-ui/icons/Lock';

import { USER_ROLES } from '@bagonboard/wiseman';
import { applySpec, prop } from 'ramda';

import { PhoneInputContainer, PhoneInputIcon } from '../../../../components';
import Button from '../../../../components/CustomButtons/Button';
import CustomInput from '../../../../components/CustomInput/CustomInput';
import CustomSelect from '../../../../components/CustomInput/CustomSelect';

import * as actions from '../../../../reducers/Users/actions';

import { constraints, roles } from '../constants';

const { Phone: PhoneInput } = Inputs;

const Container = styled.div`
  width: 550px;
  max-width: 100%;
`;

class UserDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      email        : '',
      emailError   : undefined,
      id           : '',
      name         : '',
      nameError    : undefined,
      password     : '',
      passwordError: undefined,
      role         : '',
      roleError    : undefined,
      areas        : [],
      errorAreas   : undefined,
      phone        : '',
    };
  }

  static getDerivedStateFromProps(props, state) {
    if (props.user._id !== state.id) {
      return {
        email        : props.user.email || '',
        emailError   : undefined,
        id           : props.user._id,
        name         : props.user.name || '',
        nameError    : undefined,
        password     : '',
        passwordError: undefined,
        role         : props.user.role || '',
        roleError    : undefined,
        areas        : props.user.areas || [],
        errorAreas   : undefined,
        phone        : props.user.phone || '',
      };
    }

    return null;
  }

  onInputChange = (e) => {
    const { name, value } = e.target;

    this.setState({ [name]: value, [`${name}Error`]: undefined });
  };

  onDelete = () => {
    const { deleteUserFetch, onClose } = this.props;
    const { id } = this.state;

    deleteUserFetch({ _id: id });
    onClose();
  };

  onSubmit = () => {
    const { addUserFetch, onClose, updateUserFetch } = this.props;
    const {
      email, id, name, password, role, areas, phone,
    } = this.state;

    const errors = ProviderValidate.validate(
      {
        email,
        name,
        password,
        role,
      },
      id ? { ...constraints, password: undefined } : constraints,
    );

    const properAreas = areas ? { areas } : {};
    const properPhone = phone ? { phone } : {};

    const errorAreas = role === USER_ROLES.AGENT && (!areas || areas.length === 0)
      ? 'Areas is required'
      : undefined;
    if (errors) {
      this.setState({
        emailError   : errors.email && errors.email[0],
        nameError    : errors.name && errors.name[0],
        passwordError: errors.password && errors.password[0],
        roleError    : errors.role && errors.role[0],
        errorAreas,
      });
    } else if (errorAreas) {
      this.setState({ errorAreas });
    } else if (id) {
      updateUserFetch({
        _id     : id,
        email,
        name,
        password: password || undefined,
        role,
        ...properAreas,
        ...properPhone,
      }).then(() => onClose(true));
    } else {
      addUserFetch({
        email,
        name,
        password,
        role,
        ...properAreas,
        ...properPhone,
      });
      onClose();
    }
  };

  render() {
    const { onClose, open, areas: areasData } = this.props;
    const {
      email,
      emailError,
      id,
      name,
      nameError,
      password,
      passwordError,
      role,
      roleError,
      areas,
      errorAreas,
      phone,
    } = this.state;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby="create-user-dialog"
      >
        <DialogTitle id="create-user-dialog">Create User</DialogTitle>
        <DialogContent>
          <Container>
            <CustomSelect
              data-testid="new-user-role"
              error={!!roleError}
              helpText={roleError}
              id="role"
              labelText="Role"
              formControlProps={{
                fullWidth: true,
              }}
              inputProps={{
                name    : 'role',
                onChange: this.onInputChange,
                value   : role,
              }}
              options={roles}
            />
            <CustomInput
              error={!!nameError}
              helpText={nameError}
              id="name"
              labelText="Name"
              data-testid="input-new-user-name"
              formControlProps={{
                fullWidth: true,
              }}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <AccountCircle />
                  </InputAdornment>
                ),
                name    : 'name',
                onChange: this.onInputChange,
                value   : name,
              }}
            />
            {role === USER_ROLES.AGENT && (
              <>
                <CustomSelect
                  data-testid="new-user-areas"
                  error={!!errorAreas}
                  helpText={errorAreas}
                  id="areas"
                  multiple
                  onChange={e => this.setState({
                    areas     : e.target.value,
                    errorAreas: e.target.value.length === 0 ? 'Areas is required' : undefined,
                  })}
                  labelText="Areas"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  value={areas}
                  options={areasData.map(applySpec({
                    text : prop('code'),
                    value: prop('_id'),
                  }))}
                />
                <PhoneInputContainer>
                  <PhoneInput
                    id="phone"
                    placeholder="Phone number"
                    onChange={value => this.setState({ phone: value })}
                    value={phone}
                    defaultCountry="ES"
                  />
                  <PhoneInputIcon />
                </PhoneInputContainer>
              </>
            )}
            <CustomInput
              error={!!emailError}
              helpText={emailError}
              id="email"
              labelText="Email"
              data-testid="input-new-user-email"
              formControlProps={{
                fullWidth: true,
              }}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Email />
                  </InputAdornment>
                ),
                name    : 'email',
                onChange: this.onInputChange,
                value   : email,
              }}
            />
            <CustomInput
              error={!!passwordError}
              helpText={passwordError}
              id="password"
              labelText="Password"
              data-testid="input-new-user-password"
              formControlProps={{
                fullWidth: true,
              }}
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Lock />
                  </InputAdornment>
                ),
                name    : 'password',
                onChange: this.onInputChange,
                type    : 'password',
                value   : password,
              }}
            />
          </Container>
        </DialogContent>
        <DialogActions>
          {id ? (
            <Fragment>
              <Button data-testid="button-new-user-remove" color="danger" onClick={this.onDelete} simple>
                Remove
              </Button>
              <div style={{ flex: 1 }} />
            </Fragment>
          ) : (
            <Button data-testid="button-new-user-cancel" color="info" onClick={onClose} simple>
              Cancel
            </Button>
          )}
          <Button data-testid="button-new-user-submit" color="info" onClick={this.onSubmit}>
            {id ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

UserDialog.propTypes = {
  addUserFetch   : PropTypes.func,
  deleteUserFetch: PropTypes.func,
  onClose        : PropTypes.func,
  open           : PropTypes.bool,
  updateUserFetch: PropTypes.func,
  user           : PropTypes.shape({}),
  areas          : PropTypes.arrayOf(PropTypes.any),
};

UserDialog.defaultProps = {
  addUserFetch() {},
  deleteUserFetch() {},
  onClose() {},
  open : false,
  updateUserFetch() {},
  user : {},
  areas: [],
};

const mapStateToProps = ({ areas }) => ({
  areas: areas.data,
});

const mapDispatchToProps = dispatch => ({
  addUserFetch   : user => dispatch(actions.addUserFetch(user)),
  deleteUserFetch: user => dispatch(actions.deleteUserFetch(user)),
  updateUserFetch: user => dispatch(actions.updateUserFetch(user)),
});


export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
)(UserDialog);
